import type { FileInfo } from './fileKind';

export type UniverseAssetType =
  | 'pdf'
  | 'presentation'
  | 'video'
  | 'audio'
  | 'document'
  | 'spreadsheet'
  | 'link';

export interface UniverseAsset {
  /** Opaque public id; never the database id. */
  id: string;
  type: UniverseAssetType;
  title: string;
  description: string | null;
  workspace: string | null;
  country_code: string | null;
  thumbnail_url: string | null;
  cover_url: string | null;
  preview_url: string | null;
  playback_url: string | null;
  external_url: string | null;
  access: {
    is_public: boolean;
    requires_login: boolean;
  };
  updated_at: string | null;
  created_at: string | null;
  /** Optional until the backend sends them; analytics reports them as unknown when absent. */
  content_version?: string | null;
  owner_world?: string | null;
  /** Approved Cover Studio derivative shown on the sphere, e.g. `cs-v2-1a2b3c4d`. */
  thumbnail_variant?: string | null;
  /** Publisher-set focal point (0–1) and zoom (1–2.5) for a Cover Studio derivative. */
  thumbnail_focus?: { x: number; y: number; zoom: number } | null;
}

/** What the current viewer may do with an opened asset. Decided server-side. */
export interface UniverseViewerConfig {
  content_tier: 'full' | 'preview';
  preview_page_limit: number | null;
  allow_download: boolean;
  requires_login_for_full: boolean;
}

export interface UniverseAssetDetail extends UniverseAsset {
  file_url: string | null;
  viewer: UniverseViewerConfig;
  /** Client-side: what the downloadable file actually is, when it has no backend preview. */
  file?: FileInfo;
}

export interface UniversePage {
  items: UniverseAsset[];
  next_cursor: string | null;
  has_more: boolean;
  /** Present when the server sorted the complete authorized set. */
  total?: number;
  /** `upstream` when the set was too large to sort here and stream-sansar's order is used. */
  sort_scope?: 'complete' | 'upstream';
}

export interface UniverseSearchResult {
  query: string;
  total: number;
  items: UniverseAsset[];
}

export interface UniverseCollection {
  slug: string;
  name: string;
  workspace: string | null;
  deck_count: number;
}

export type UniverseSort = 'relevant' | 'newest' | 'updated' | 'az' | 'za';

export class UniverseApiError extends Error {
  readonly status: number;

  constructor(message: string, status: number) {
    super(message);
    this.name = 'UniverseApiError';
    this.status = status;
  }
}
